import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import dayjs from "dayjs";
import axios from "axios";
import Swal from "sweetalert2";
import SecureLS from "secure-ls";

const ShiftInfo = ({ user, posId, token, navigate }) => {
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [editedStart, setEditedStart] = useState(null);
  const [editedEnd, setEditedEnd] = useState(null);

  const ls = new SecureLS({ encodingType: "aes" });

  useEffect(() => {
    const fetchShift = async () => {
      try {
        if (!posId || !token || user.role === "Superadmin") return;

        const today = dayjs().format("YYYY-MM-DD");
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/shift/fetch`,
          { posId, date: today, userId: user.userId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data.shifts && res.data.shifts.length > 0) {
          const latest = res.data.shifts[res.data.shifts.length - 1];
          setShift(latest);
        }
      } catch (err) {
        console.error("Error fetching shift:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchShift();
  }, [user, posId, token]);

  const handleStartShift = async () => {
    const now = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const confirm = await Swal.fire({
      title: "Start Shift?",
      html: `
        <div style="text-align:left;">
          <strong>User:</strong> ${user.firstname} ${user.lastname}<br/>
          <strong>Current Time:</strong> ${now}<br/><br/>
          Are you sure you want to start your shift now?
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Start Shift",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/shift/start`,
        {
          posId,
          userId: user.userId,
          cashierName: `${user.firstname} ${user.lastname}`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShift(res.data.shift);
    } catch (err) {
      console.error("Start shift error:", err);
      Swal.fire("Error", "Failed to start shift.", "error");
    }
  };

  const handleEndShift = async () => {
    const now = dayjs().format("YYYY-MM-DD HH:mm:ss");

    const confirm = await Swal.fire({
      title: "End Shift?",
      html: `
        <div style="text-align:left;">
          <strong>User:</strong> ${user.firstname} ${user.lastname}<br/>
          <strong>Current Time:</strong> ${now}<br/><br/>
          Are you sure you want to end your shift now?
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, End Shift",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/shift/end`,
        { shiftId: shift._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShift(res.data.shift);
      Swal.fire("Shift Ended", "", "success");
    } catch (err) {
      console.error("End shift error:", err);
      Swal.fire("Error", "Failed to end shift.", "error");
    }
  };

  const handleValidateOTP = async () => {
    const { value: otp } = await Swal.fire({
      title: "OTP Required",
      input: "text",
      inputLabel: "Enter Manager's OTP to Edit Shift",
      inputPlaceholder: "Enter OTP",
      inputAttributes: {
        autocapitalize: "off",
        autocorrect: "off",
      },
      showCancelButton: true,
    });

    if (!otp) return;

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/otp/validate`,
        { otp, action: "Edit Shift" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const successMessage = "OTP validated successfully";

      if (res.status === 200 && res.data.message === successMessage) {
        ls.set("otpValidatedForShiftEdit", true);

        setEditedStart(
          shift?.startShift
            ? dayjs(shift.startShift).format("YYYY-MM-DDTHH:mm")
            : ""
        );
        setEditedEnd(
          shift?.endShift
            ? dayjs(shift.endShift).format("YYYY-MM-DDTHH:mm")
            : ""
        );
        setIsEditing(true);
        Swal.fire("OTP Validated", "", "success");
      } else {
        Swal.fire("Invalid OTP", res.data.message || "", "error");
      }
    } catch (err) {
      console.error("OTP verification failed:", err);
      Swal.fire("Error verifying OTP", "", "error");
    }
  };

  const handleSaveShift = async () => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/shift/edit`,
        {
          shiftId: shift._id,
          startShift: editedStart,
          endShift: editedEnd,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShift(res.data.shift);
      setIsEditing(false);
      Swal.fire("Shift updated successfully", "", "success");
    } catch (err) {
      console.error("Error updating shift:", err);
      Swal.fire("Error saving shift", "", "error");
    }
  };

    if (user.role === "Superadmin") return null;

  return (
    <Box display="flex" justifyContent="center" mb={1} position="relative">
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 800,
          height: "100%",
          minHeight: 100,
          border: "2px solid",
          borderColor: "primary.dark",
          borderRadius: 2,
          padding: 1.5,
          filter: loading ? "blur(3px)" : "none",
          pointerEvents: loading ? "none" : "auto",
          position: "relative",
        }}
      >
        <CardContent sx={{ padding: "12px !important" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1.5}
          >
            <Typography variant="body2">
              <strong>User:</strong> {user.firstname} {user.lastname}
            </Typography>

            {isEditing ? (
              <>
                <TextField
                  type="datetime-local"
                  label="Start Shift"
                  size="small"
                  value={editedStart}
                  onChange={(e) => setEditedStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="datetime-local"
                  label="End Shift"
                  size="small"
                  value={editedEnd}
                  onChange={(e) => setEditedEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            ) : (
              <>
                <Typography variant="body2">
                  <strong>Start Shift:</strong>{" "}
                  {shift?.startShift
                    ? dayjs(shift.startShift).format("YYYY-MM-DD HH:mm")
                    : "--"}
                </Typography>
                <Typography variant="body2">
                  <strong>End Shift:</strong>{" "}
                  {shift?.endShift
                    ? dayjs(shift.endShift).format("YYYY-MM-DD HH:mm")
                    : "--"}
                </Typography>
              </>
            )}

            <Box display="flex" alignItems="center" gap={1}>
              {isEditing ? (
                <IconButton
                  color="success"
                  size="small"
                  onClick={handleSaveShift}
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
              ) : (
                <>
                  {!shift?.startShift && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleStartShift}
                    >
                      Start Shift
                    </Button>
                  )}

                  {shift?.startShift && !shift?.endShift && (
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={handleEndShift}
                    >
                      End Shift
                    </Button>
                  )}

                  {shift?.startShift && (
                    <IconButton
                      color="default"
                      size="small"
                      onClick={handleValidateOTP}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={10}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255, 255, 255, 0.6)"
          borderRadius={2}
        >
          <Typography variant="body2" mb={1} fontWeight="bold">
            Fetching Shifts...
          </Typography>
          <Box className="loader">
            <span className="MuiCircularProgress-root MuiCircularProgress-indeterminate MuiCircularProgress-colorPrimary MuiCircularProgress-sizeMd" />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ShiftInfo;
